import './axiosRequestConfigAugmentation';

import { MGMT_API_BASE_URL } from '@openx/constants/api';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';
import { buildStorage, setupCache } from 'axios-cache-interceptor';
import localforage from 'localforage';
import type { AnyAction, Store } from 'redux';

import { asyncSleep } from '../lib/asyncSleep';
import { getXOpenxInstance, getXOpenxInstanceHeader } from '../lib/getXOpenxInstance';
import { getOIDCAuthInfoFromStorage } from '../lib/oidc/getOIDCAuthInfoFromStorage';
import { redirectToForbiddenPage } from '../routes/redirectToForbiddenPage';
import { logoutOnError } from './logoutOnError';

type Notification = {
  message: string;
  options: Record<'variant', string>;
};

export type CreateApiParams = {
  reduxStore: Store;
  clientHeader: string;
  clientHeaderValue: string;
  pageHeader: string;
  enqueueNotification: (data: Notification) => AnyAction;
  cachePaths: RegExp[];
  cacheAge: number;
  localForageName: string;
  cacheKeyPrefix?: string | (() => string);
};

async function dispatchErrorNotification(
  store: Store,
  enqueueNotification: CreateApiParams['enqueueNotification'],
  error: AxiosError
): Promise<void> {
  const message = error.response?.data?.['message'] || error.response?.data?.['err_message'];
  store.dispatch(
    enqueueNotification({
      message: message ?? error.message,
      options: { variant: 'error' },
    })
  );

  await asyncSleep(300);
}

export const createApi = ({
  reduxStore,
  clientHeader,
  clientHeaderValue,
  pageHeader,
  enqueueNotification,
  cachePaths,
  cacheAge,
  localForageName,
  cacheKeyPrefix = '',
}: CreateApiParams): { api: AxiosInstance } => {
  const store = localforage.createInstance({
    driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
    name: localForageName,
  });

  setupCache(axios, {
    cachePredicate: {
      ignoreUrls: cachePaths,
    },
    cacheTakeover: false,
    generateKey: req => {
      const prefix = typeof cacheKeyPrefix === 'function' ? cacheKeyPrefix() : cacheKeyPrefix;

      return (
        `${prefix}.${getXOpenxInstance(true)}${req.url}` +
        (req.params ? JSON.stringify(req.params) : '') +
        // store ja_JP locale options as separate keys
        ((req.url?.includes('/options/') && req.headers?.['Accept-Language']) || '')
      );
    },
    interpretHeader: false,
    storage: buildStorage({
      async find(key) {
        const value = await store.getItem<string | null>(key);
        if (!value) {
          return;
        }
        return JSON.parse(value);
      },

      async remove(key) {
        await store.removeItem(key);
      },

      async set(key, value) {
        await store.setItem(key, JSON.stringify(value));
      },

      clear: async () => {
        await store.clear();
      },
    }),
    ttl: cacheAge,
    update: axios => {
      if (axios.config.method !== 'get') {
        store.clear();
      }
    },
  });

  axios.defaults.baseURL = MGMT_API_BASE_URL;
  axios.defaults.headers.common = {
    'Cache-Control': 'no-cache',
    [clientHeader]: clientHeaderValue,
  };

  const apigeeBaseURLRegexp = /\/ox\/4\.0|\/login/;

  axios.interceptors.request.use(async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    config.headers = config.headers || ({} as AxiosRequestHeaders);
    config.headers[pageHeader] = window.location.pathname;

    // if user is authenticated with new sso we need to change some axios properties
    const oidcAuthInfo = await getOIDCAuthInfoFromStorage();

    if (oidcAuthInfo) {
      const shouldHaveApigeeBaseUrl = apigeeBaseURLRegexp.test(config.baseURL || '');

      config.baseURL = shouldHaveApigeeBaseUrl ? `/apigee${MGMT_API_BASE_URL}` : config.baseURL;
      config.headers.Authorization = `Bearer ${oidcAuthInfo.accessToken}`;
      config.headers['x-openx-instance'] = getXOpenxInstanceHeader(oidcAuthInfo.instanceUid);
    }

    return config;
  });

  axios.interceptors.response.use(
    response => response,
    async (error: AxiosError): Promise<void> => {
      const serverErrors = [500, 503, 525];
      const disableGlobalNotification = error.config?.disableGlobalNotification;

      if (error.response?.status === 401) {
        if (!disableGlobalNotification) {
          await dispatchErrorNotification(reduxStore, enqueueNotification, error);
        }
        logoutOnError();

        // we have to wait until the browser redirects the sso-redirect page
        await asyncSleep(1000);
      } else if (error.response?.status === 403) {
        if (!error.config?.skipGlobalForbiddenHandling) {
          if (!disableGlobalNotification) {
            await dispatchErrorNotification(reduxStore, enqueueNotification, error);
          }
          redirectToForbiddenPage();

          // we have to wait until the browser redirects the forbidden page
          await asyncSleep(1000);
        }
      }

      if (!disableGlobalNotification && error.response && serverErrors.includes(error.response.status)) {
        // A special hack for custom error handling in AuthService proxyed by the API,
        // I will be crucified for this in CR for sure, it's comforting that it will be removed one day
        const isSsoUserCreatingError =
          Array.isArray(error.response?.data) &&
          error.response?.data.some(i => i.message.includes('User creation on old SSO is disabled'));
        const message = isSsoUserCreatingError
          ? 'Cannot create users using old SSO. Login with login.openx.com to perform this action.'
          : error.message;

        reduxStore.dispatch(
          enqueueNotification({
            message,
            options: { variant: 'error' },
          })
        );
      }

      throw error;
    }
  );

  const api = axios;

  return { api };
};
